<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import { Chrome } from '@lk77/vue3-color';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { isHexColor } from 'dashboard/helper/labelColor';
import { useVuelidate } from '@vuelidate/core';

import NextButton from 'dashboard/components-next/button/Button.vue';

// digitt: DENG-7201 — a label marker can be a color or an emoji
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    NextButton,
    EmojiIconPicker,
    OnClickOutside,
    Chrome,
  },
  props: {
    selectedResponse: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      title: '',
      description: '',
      showOnSidebar: true,
      color: '',
      showEmojiPicker: false,
      showColorPicker: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    // digitt: DENG-7201 — current marker is an emoji when it isn't a hex color
    isEmojiColor() {
      return !isHexColor(this.color);
    },
    pageTitle() {
      return `${this.$t('LABEL_MGMT.EDIT.TITLE')} - ${
        this.selectedResponse.title
      }`;
    },
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    // digitt: DENG-7201 — color and emoji are both stored in `color`; last choice wins
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    onPickEmoji(emoji) {
      this.color = emoji;
      this.showEmojiPicker = false;
    },
    toggleColorPicker() {
      this.showColorPicker = !this.showColorPicker;
    },
    onPickColor(value) {
      this.color = value.hex;
    },
    setFormValues() {
      this.title = this.selectedResponse.title;
      this.description = this.selectedResponse.description;
      this.showOnSidebar = this.selectedResponse.show_on_sidebar;
      this.color = this.selectedResponse.color;
    },
    editLabel() {
      this.$store
        .dispatch('labels/update', {
          id: this.selectedResponse.id,
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        })
        .then(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          setTimeout(() => this.onClose(), 10);
        })
        .catch(() => {
          useAlert(this.$t('LABEL_MGMT.EDIT.API.ERROR_MESSAGE'));
        });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-wrap mx-0" @submit.prevent="editLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />
      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full mb-4">
        <label>{{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}</label>
        <!-- digitt: DENG-7201 — choose a color OR an emoji (last choice wins) -->
        <div class="flex gap-2 items-center">
          <OnClickOutside class="relative" @trigger="showColorPicker = false">
            <NextButton
              faded
              slate
              sm
              icon="i-lucide-pipette"
              type="button"
              @click="toggleColorPicker"
            />
            <Chrome
              v-if="showColorPicker"
              disable-alpha
              :model-value="isEmojiColor ? '#1f93ff' : color"
              class="colorpicker--chrome"
              @update:model-value="onPickColor"
            />
          </OnClickOutside>
          <OnClickOutside class="relative" @trigger="showEmojiPicker = false">
            <NextButton
              faded
              slate
              sm
              icon="i-ph-smiley"
              type="button"
              @click="toggleEmojiPicker"
            />
            <EmojiIconPicker
              v-if="showEmojiPicker"
              mode="emoji"
              class="emoji-dialog left-[40px] top-[-180px]"
              @select="onPickEmoji($event.value)"
            />
          </OnClickOutside>
          <span
            class="flex items-center justify-center flex-shrink-0 w-8 h-8 border border-solid rounded-lg border-n-strong"
          >
            <span v-if="isEmojiColor" class="text-lg leading-none">
              {{ color }}
            </span>
            <span
              v-else
              class="rounded size-4"
              :style="{ background: color }"
            />
          </span>
        </div>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('LABEL_MGMT.FORM.EDIT')"
          :disabled="v$.title.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  :deep(input) {
    @apply lowercase;
  }
}

// digitt: DENG-7201 — position the color picker popover below its button
.colorpicker--chrome.vc-chrome {
  @apply shadow-lg bottom-full mb-2 absolute z-[9999] border border-solid border-n-weak rounded;

  ::v-deep {
    input {
      @apply bg-white dark:bg-white;
    }
  }
}

// digitt: DENG-7201 — hide EmojiInput's pointer arrow (doesn't align in this layout)
:deep(.emoji-dialog)::before {
  display: none;
}
</style>
